import { useEffect, useMemo } from 'react';
import * as Checkbox from '@radix-ui/react-checkbox';
import { useForm } from 'react-hook-form';

import { IndexDTO } from '@/types/api-types';

import IconClose from '@/assets/svg/icon-close.svg?react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { useGlobalStore } from '@/stores/globalStore';

import {
  INDEX_OBJECT_TYPES,
  INSURANCE_AREA,
} from '@/constants/global-constants';

interface AddIndexDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingIndex?: IndexDTO | null;
  onClose: () => void;
  onAdd: (index: IndexDTO) => void;
  onEdit: (index: IndexDTO) => void;
}

export function AddIndexDialog({
  open,
  onOpenChange,
  editingIndex,
  onClose,
  onAdd,
  onEdit,
}: AddIndexDialogProps) {
  const { indexes } = useGlobalStore();
  const visibleIndexes = useMemo(() => {
    return indexes.filter((index) => !index.is_deleted);
  }, [indexes]);

  const form = useForm<IndexDTO>({
    defaultValues: {
      name: editingIndex?.name || '',
      insurance_area: editingIndex?.insurance_area || undefined,
      insurance_object_type: editingIndex?.insurance_object_type || undefined,
      description: editingIndex?.description || '',
      auto_archive: editingIndex?.auto_archive || false,
    },
  });

  const handleClose = () => {
    onClose();
    onOpenChange(false);
  };

  const handleAdd = () => {
    onAdd(form.getValues());
  };

  const handleEdit = () => {
    onEdit(form.getValues());
  };

  useEffect(() => {
    if (editingIndex) {
      form.reset(editingIndex);
    } else {
      form.reset({});
    }
  }, [editingIndex, form, open]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[432px] p-0">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(editingIndex ? handleEdit : handleAdd)}
          >
            <DialogHeader className="p-5 pb-0">
              <div className="flex items-center justify-between">
                <DialogTitle className="font-montserrat text-2xl font-semibold text-primary">
                  {editingIndex ? 'Редагувати індекс' : 'Новий індекс'}
                </DialogTitle>
              </div>
            </DialogHeader>

            <DialogDescription hidden>
              {editingIndex ? 'Редагувати індекс' : 'Новий індекс'}
            </DialogDescription>

            <div className="space-y-4 p-4">
              <FormField
                control={form.control}
                name="name"
                rules={{
                  required: "Індекс є обов'язковим полем",
                  validate: (value) => {
                    // Skip validation if we're editing and the name hasn't changed
                    if (editingIndex && editingIndex.name === value) {
                      return true;
                    }

                    // Check if the name is unique among existing indexes
                    const nameExists = visibleIndexes.some(
                      (index) =>
                        index.name.toLowerCase().trim() ===
                        value.toLowerCase().trim(),
                    );
                    return nameExists
                      ? 'Індекс з такою назвою вже існує'
                      : true;
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Індекс"
                        className="h-[50px] rounded-md border-separator"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="insurance_area"
                rules={{ required: "Напрямок є обов'язковим полем" }}
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-[50px] rounded-md border-separator">
                          <SelectValue placeholder="Напрямок" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={INSURANCE_AREA.INSURANCE}>
                          <span>{INSURANCE_AREA.INSURANCE}</span>
                        </SelectItem>
                        <SelectItem value={INSURANCE_AREA.SETTLEMENT}>
                          <span>{INSURANCE_AREA.SETTLEMENT}</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="insurance_object_type"
                rules={{ required: "Тип ОС є обов'язковим полем" }}
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-[50px] rounded-md border-separator">
                          <SelectValue placeholder="Тип ОС" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={INDEX_OBJECT_TYPES.VEHICLE}>
                          {INDEX_OBJECT_TYPES.VEHICLE}
                        </SelectItem>
                        <SelectItem value={INDEX_OBJECT_TYPES.PROPERTY}>
                          {INDEX_OBJECT_TYPES.PROPERTY}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                rules={{ required: "Опис є обов'язковим полем" }}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Опис"
                        className="min-h-[175px] resize-none rounded-md border-separator"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="auto_archive"
                render={({ field }) => (
                  <FormItem>
                    <label className="flex cursor-pointer items-center justify-between gap-2">
                      <span className="text-sm font-medium leading-none">
                        Автоматичне архівування документів
                      </span>
                      <Checkbox.Root
                        className="flex h-12 w-12 items-center justify-center rounded-md border border-gray-200"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      >
                        <Checkbox.Indicator>
                          <IconClose className="h-4 w-4" />
                        </Checkbox.Indicator>
                      </Checkbox.Root>
                    </label>
                  </FormItem>
                )}
              />

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  variant="outline"
                  disabled={!form.formState.isValid}
                >
                  Зберегти
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
